import React, {
	useEffect,
	useState,
	useMemo,
	useRef,
	useCallback,
} from "react";
import DefaultSelect from "../../components/form/DefaultSelect";
import { Button, Divider, Input, Modal } from "antd";
import DefaultDatePicker from "../../components/form/DefaultDatePicker";
import { AgGridReact } from "ag-grid-react";
import api from "../../utils/axios";
import { ColDef } from "ag-grid-community";
import { CheckCircleOutlined } from "@ant-design/icons";
import { priceFormatter } from "../../utils/formatters";
import { documentTypesLookup } from "../../lib/cachedLookups";
import useLookup from "../../hooks/useLookup";
import useCashRegisterOptions from "../../hooks/useCashRegisterOptions";
import { fetchZonesCached } from "../../lib/cachedLookups";
import { toast } from "react-toastify";
import { ExpenseType } from "../../types/ExpenseType";
import classes from "./AdminFinance.module.css";
import DefaultInput from "../../components/form/DefaultInput";
import { ReactComponent as PrintIcon } from "../../assets/print-icon.svg";
import PaginationTable from "../../components/Pagination";
import Buttons from "../../components/form/Buttons";
import {
	exportDataToCsv,
	moneyExportColumns,
} from "../../utils/exportDataToCsv";
import useCurrencies from "../../hooks/useCurrencies";
import DefaultPhoneInput from "../../components/form/DefaultPhoneInput";
import DefaultSwitch from "../../components/form/DefaultSwitch";
import { fetchAllUsers } from "../../lib/UserApi";
import useOnDemand from "../../hooks/useOnDemand";
import { userRoles } from "../../types/enums";
import useLang from "../../hooks/useLang";
import { fetchAllPages, usePagedList } from "../../hooks/usePagedList";
import { ReactComponent as Plus } from "../../assets/add-plus.svg";
import { ReactComponent as Export } from "../../assets/Export-Icon.svg";
import { ReactComponent as ExcelIcon } from "../../assets/excel-icon.svg";
import { DateTime } from "luxon";
import { useMediaQuery } from "@mui/material";
import { hasAccess } from "../../lib/MenuValidation";
import { CONFIG } from "../../config";

const { TextArea } = Input;
const AdminFinance = () => {
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	let grid = useRef(null);
	const [countryDropDown, setCountryDropDown] = useState<any>([]);
	const [selectedCountry, setSelectedCountry] = useState<any>(null);
	const [internationalDriverDropDown, setInternationalDriverDropDown] =
		useState<any>([]);
	const [internationalSettlements, setInternationalSettlements] = useState<
		any[]
	>([]);
	const [
		selectedSettlementInternationalImport,
		setSelectedSettlementInternationalImport,
	] = useState<any>(null);
	const [documentType, setDocumentType] = useState<string | undefined>();
	const [cashRegisterId, setCashRegisterId] = useState(0);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>();
	const [value, setValue] = useState(0);
	const [expenseReason, setExpenseReason] = useState("");
	const cashRegisterDropDown = useCashRegisterOptions();
	const { data: documentTypes } = useLookup(documentTypesLookup);
	const documentTypeDropDown = useMemo(
		() =>
			(documentTypes ?? []).map((documentType) => ({
				value: documentType.id,
				label: documentType.description,
				documentTypeGroupId: documentType.documentTypeGroupId,
			})),
		[documentTypes]
	);
	const [open, setOpen] = useState<boolean>(false);
	const [expenseValidationParams, setExpenseValidationParams] =
		useState<ExpenseType>();
	const [companyBalance, setCompanyBalance] = useState<any>([]);
	const [editedRows, setEditedRows] = useState<any>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [showButtons, setShowButtons] = useState<boolean>(false);
	const [fromDate, setFromDate] = useState<any>(null);
	const [toDate, setToDate] = useState<any>(null);
	const [selectedDocumentType, setSelectedDocumentType] = useState<
		string | undefined
	>();
	const [expenseStatus, setExpenseStatus] = useState<string>("");
	const [openSubjectModal, setOpenSubjectModal] = useState<boolean>(false);
	const [openExpenseModalForDrivers, setOpenExpenseModalForDrivers] =
		useState<boolean>(false);
	const [description, setDescription] = useState<string>("");
	const [fiscalNo, setFiscalNo] = useState<string>("");
	const [vatNo, setVatNo] = useState<string>("");
	const [phone, setPhone] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [address, setAddress] = useState<string>("");
	const [status, setStatus] = useState<boolean>(false);
	const [expense, setExpense] = useState<boolean>(true);
	const [earning, setEarning] = useState<boolean>(false);
	const [subjectId, setSubjectId] = useState<any>(null);
	const [driverId, setDriverId] = useState<any>(null);
	const [subjectsDropDown, setSubjectsDropDown] = useState<any[]>();
	const [modalValue, setModalValue] = useState(0);
	const [modalDescription, setModalDescription] = useState("");
	// Every user, only to list the drivers in the driver-expense window: loaded when that window
	// opens instead of on every visit to this page.
	const { data: allUsers, load: loadAllUsers } = useOnDemand(fetchAllUsers);
	const driverDropDown = useMemo(
		() =>
			(allUsers ?? [])
				.filter((user: any) => user.userRoleId === userRoles.DRIVER)
				.map((user: any) => ({
					value: user.id,
					label: `${user.firstName} ${user.lastName} - ${user.userRoleDescription}`,
				})),
		[allUsers]
	);
	const [selectedCashRegisterId, setSelectedCashRegisterId] = useState<
		any | null
	>(null);
	const [openInternationalPaymentsModal, setOpenInternationalPaymentsModal] =
		useState<boolean>(false);
	const [dateFrom, setDateFrom] = useState<DateTime | null>(null);
	const [dateTo, setDateTo] = useState<DateTime | null>(null);

	// The API filters and pages; a range that ends before it starts holds nothing, as before.
	const expenseParams = useMemo(
		() =>
			fromDate && toDate && fromDate.isAfter(toDate, "day")
				? null
				: {
						currentPage,
						pageSize,
						fromDate: fromDate?.format("YYYY-MM-DD"),
						toDate: toDate?.format("YYYY-MM-DD"),
						documentTypeId: selectedDocumentType,
						completed:
							expenseStatus === "" ? undefined : expenseStatus,
				  },
		[
			currentPage,
			pageSize,
			fromDate,
			toDate,
			selectedDocumentType,
			expenseStatus,
		]
	);
	const expenses = usePagedList<any>(
		"Expenses/select-expenses",
		expenseParams,
		setCurrentPage
	);

	// Unsaved edits live in the rows on screen; a page or filter change would replace them.
	const canChangePage = () => {
		if (!showButtons) return true;
		// rc-pagination reports a page-size change twice; one toast is enough.
		toast.warning(
			"Ruani ose anuloni ndryshimet para se të ndryshoni faqen ose filtrin.",
			{ toastId: "unsaved-edits" }
		);
		return false;
	};

	const onPageChanged = (page: any) => {
		if (!canChangePage()) return;
		setCurrentPage(page);
	};
	const handlePageSizeChange = (current: number, size: number) => {
		if (!canChangePage()) return;
		setPageSize(size);
		setCurrentPage(1);
	};

	const { strings } = useLang("adminFinance");
	const { dualCurrencyEnabled, defaultCurrencyId } = useCurrencies();

	const canCreateExpenses = hasAccess("CREATE_FINANCE_EXPENSE_ACCESS");
	const canCreateEarning = hasAccess("CREATE_FINANCE_EARNING_ACCESS");
	const canValidatedFinancialDocumentd = hasAccess("VALIDATE_FINANCE_ACCESS");
	const canCreateSupplier = hasAccess("SUPPLIER_REGISTRATION_ACCESS");

	const getSubjects = async () => {
		try {
			const response = await api.get("Subject/select-subjects");
			if (response.status === 200) {
				const result = response?.data;
				const subjects = result?.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
				setSubjectsDropDown(subjects);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const clearState = () => {
		setDocumentType("");
		setValue(0);
		setExpenseReason("");
		setCashRegisterId(0);
		setSubjectId(null);
	};
	const [internationalOrdersToImport, setInternationalOrdersToImport] =
		useState([]);
	const [
		internationalOrdersToImportColDefs,
		setInternationalOrdersToImportColDefs,
	] = useState([]);

	const getCompanyFinance = async () => {
		try {
			const response = await api.get("Finance/company-finance");
			if (response) {
				const result = response.data[0];
				setCompanyBalance(result);
			}
		} catch (error) {
			console.log(error);
			setCompanyBalance([]);
		}
	};
	const isEditable = (params: any): boolean => {
		return !params.data.completed;
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	// Data loads once on open. It used to share the column effect below, so a language switch
	// reloaded the expenses, subjects and company finance.
	useEffect(() => {
		getSubjects();
		getCompanyFinance();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!strings) {
			return;
		}
		setColumnDefs([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 38,
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: strings?.createdAt,
				field: "createdAt",
				// valueFormatter: (params) => valueDateFormatter(params?.value),
				width: 240,
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: strings?.value,
				field: "value",
				valueFormatter: (params) => priceFormatter(params?.value),
				width: 240,
				editable: isEditable,
				valueSetter: (params) => {
					const newValue = parseFloat(params.newValue);
					if (isNaN(newValue) || newValue < 0) {
						toast.error("Shuma duhet të jetë një numër pozitiv.");
						return false;
					}
					params.data.value = newValue;
					return true;
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: strings?.type,
				field: "documentTypeDescription",
				width: 240,
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: strings?.description,
				field: "expenseReason",
				width: 240,
				editable: isEditable,
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: strings?.clientDetails,
				width: 300,
				valueGetter: (params) => {
					const { firstName, lastName, earningReason } = params.data;
					if (firstName && lastName && earningReason) {
						return `(${firstName} ${lastName}) ${earningReason}`;
					}
					return "";
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: strings?.valido,
				field: "completed",
				width: 240,
				cellEditorParams: { values: [true, false] },
				cellRenderer: (params: any) => {
					if (
						params.value === false &&
						canValidatedFinancialDocumentd
					) {
						return (
							<Button
								type="primary"
								// className="bg-blue-600"
								style={{ backgroundColor: "#4F16C2" }}
								icon={<CheckCircleOutlined />}
							>
								{strings?.validDocument}
							</Button>
						);
					} else if (
						!canValidatedFinancialDocumentd &&
						params.value === false
					) {
						return strings?.nonValidDocument;
					}
					return strings?.validated;
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: strings?.subjectDescription,
				field: "subjectDescription",
				hide: true,
			},
		]);
	}, [strings]);

	const [validateExpenseSubmitting, setValidateExpenseSubmitting] =
		useState<boolean>(false);

	const ValidateExpenseAndCreateCashRegister = async () => {
		try {
			setValidateExpenseSubmitting(true);
			const response = await api.post(
				"CashRegisterTransaction/update-expense-with-transaction",
				expenseValidationParams
			);
			if (response.status === 200) {
				await expenses.reload();
				setOpen(false);
				toast.success("Validimi u krye me sukses!");
				clearState();
			}
		} catch (error) {
			console.log(error);
		} finally {
			setValidateExpenseSubmitting(false);
		}
	};

	const onCompletedButtonClick = async (rowData: any) => {
		if (rowData.completed === false) {
			if (!cashRegisterId || cashRegisterId === 0) {
				toast.error("Nuk keni zgjedhur nje Cash Register");
				return;
			}
			setExpenseValidationParams({
				expenseId: rowData?.id,
				cashOut: rowData.expense ? rowData?.value : 0.0,
				cashIn: rowData.expense ? 0.0 : rowData?.value,
				documentTypeId: rowData?.documentTypeId,
				currencyId: rowData?.currencyId,
				comment: rowData?.expenseReason,
				cashRegisterId: cashRegisterId,
				expense: rowData?.expense,
			});
			setOpen(true);
		}
	};

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "completed":
				await onCompletedButtonClick(params.data);
				break;
		}
	};

	const onSelectClear = () => {
		setCashRegisterId(0);
	};

	const onBtExport = useCallback(() => {
		if (grid) {
			(grid.current as any).api.exportDataAsExcel();
		}
	}, []);

	const onRowEdit = async ({ data }: any) => {
		// if (event.type === "rowValueChanged") {
		setShowButtons(true);
		// An expense, an earning and a cash-register row can share an id; `expense` tells them apart.
		setEditedRows((prevState: any) => [
			...prevState.filter(
				(row: any) =>
					row.id !== data?.id || row.expense !== data?.expense
			),
			data,
		]);
		// }
	};
	const [saveEditedRowsSubmitting, setSaveEditedRowsSubmitting] =
		useState<boolean>(false);

	const saveEditedRows = async () => {
		setSaveEditedRowsSubmitting(true);
		if (!editedRows) {
			toast.error("Nuk keni asnje ndryshim!");
			setSaveEditedRowsSubmitting(false);
			return;
		}
		const editedExpneses = editedRows?.map((item: any) => ({
			value: item.value,
			expenseReason: item.expenseReason,
			id: item.id,
			expense: item.expense,
		}));
		if (!editedExpneses) {
			toast.error("Error");
			setSaveEditedRowsSubmitting(false);
			return;
		} else {
			try {
				const response = await api.post(
					"Expenses/expense-update",
					editedExpneses
				);
				if (response.status === 200) {
					cancleEditedRows();
					toast.success(
						`Me sukses u perditesuan ${editedExpneses.length} ${
							editedExpneses.length > 1 ? "rreshta" : "rresht"
						}`
					);
				}
			} catch (error) {
				console.log("Error:" + error);
				toast.error("Error:" + error);
			} finally {
				setSaveEditedRowsSubmitting(false);
			}
		}
	};

	const cancleEditedRows = async () => {
		setShowButtons(false);
		setEditedRows([]);
		await expenses.reload();
	};

	const [expenseStatusOptions] = useState([
		{ label: "Te pa validuara", value: false },
		{ label: "Te validuara", value: true },
	]);

	const selectedColumnIds = [
		"createdAt",
		// A finance document carries its own `currencyId` — a refusal fee booked against a EUR
		// order is a EUR document — so on PostmanAL the amount goes out as a number and the
		// currency gets a column of its own. `value` is no longer pre-formatted in the map below:
		// `priceFormatter` would turn it into text, and text cannot be read back as an amount.
		...moneyExportColumns({
			colId: "value",
			currencyHeaderName: strings?.currencyColumn,
			currency: (row: any) => row?.currencyId ?? defaultCurrencyId,
			dualCurrencyEnabled,
			// Off PostmanAL the cell keeps the formatted string it has always had.
			single: (row: any) => priceFormatter(row?.value),
		}),
		"documentTypeDescription",
		"expenseReason",
		"completed",
		"subjectDescription",
	];

	const onExportSelectedId = async () => {
		// As before: ticked rows if any; with a filter, every match (all pages); else the page shown.
		const ticked = (grid.current as any)?.api?.getSelectedRows()?.length;
		const filtered =
			fromDate || toDate || selectedDocumentType || expenseStatus !== "";
		let rows: any[] = expenses.items;
		try {
			if (!ticked && filtered && expenseParams) {
				rows = await fetchAllPages<any>(
					"Expenses/select-expenses",
					expenseParams
				);
			}
		} catch (error) {
			console.log(error);
			return;
		}
		const dataToExport = rows.map((item: any) => ({
			...item,
			completed:
				!canValidatedFinancialDocumentd && !item.completed
					? strings?.nonValidDocument
					: strings?.validated,
		}));

		exportDataToCsv(grid, selectedColumnIds, dataToExport);
	};
	const onFirstDataRendered = useCallback((params: any) => {
		(grid.current as any).api.sizeColumnsToFit();
	}, []);

	const validateSubjectInsertedData = (subjectData: any) => {
		if (!subjectData?.description) {
			toast.error("Përshkrimi është i detyrueshëm");
			return false;
		}

		if (!subjectData?.fiscalNo) {
			toast.error("Numri fiskal është i detyrueshëm");
			return false;
		}

		if (!subjectData?.vatNo) {
			toast.error("Numri i TVSH-së është i detyrueshëm");
			return false;
		}

		if (!subjectData?.phone) {
			toast.error("Numri i telefonit është i detyrueshëm");
			return false;
		}

		return true;
	};

	const [subjectInsertSubmitting, setSubjectInsertSubmitting] =
		useState<boolean>(false);

	const subjectInsert = async () => {
		setSubjectInsertSubmitting(true);

		if (!canCreateSupplier) {
			toast.error("Nuk keni akses për të regjistruar një furnitor.");
			setSubjectInsertSubmitting(false);
			return;
		}

		const subjectData = {
			description: description,
			fiscalNo: fiscalNo,
			vatNo: vatNo,
			phone: phone,
			email: email,
			address: address,
			status: status,
		};
		if (validateSubjectInsertedData(subjectData)) {
			try {
				const response = await api.post(
					"Subject/insert-subject",
					subjectData
				);
				if (response.status === 200) {
					setOpenSubjectModal(false);
					await getSubjects();
					toast.success(
						"Regjisitrimi i subjektit u perfundua me sukses!"
					);
				}
			} catch (error: any) {
				console.error("An Error occured:" + error.response.data);
			} finally {
				setSubjectInsertSubmitting(false);
			}
		} else {
			setSubjectInsertSubmitting(false);
			return;
		}
	};

	const handleSwitchChange = (checked: boolean) => {
		setExpense((prevState) => !prevState);
		setEarning((prevState) => !prevState);
	};
	const validateExpenses = (data: any) => {
		const { documentTypeId, value, expenseReason, subjectId } = data;
		if (!value || !expenseReason || !documentTypeId || !subjectId) {
			if (!value) toast.error("Shuma eshte e kerkuar.");
			if (!expenseReason) toast.error("Pershkrimi eshte i kerkuar.");
			if (!documentTypeId) toast.error("Lloji eshte i kerkuar.");
			if (!subjectId) toast.error("Furnitori eshte i kerkuar.");
			return false;
		}
		return true;
	};
	const [expenseIsSubmitting, setExpenseIsSubmitting] = useState(false);

	const submitExpense = async () => {
		if (expenseIsSubmitting) return;
		setExpenseIsSubmitting(true);
		const data = {
			documentTypeId: documentType,
			value: value,
			expenseReason: expenseReason,
			subjectId: subjectId,
		};
		if (validateExpenses(data)) {
			try {
				const url = expense
					? "Expenses/insert-new-expense"
					: "Earnings/insert-new-earning";
				const response = await api.post(url, data);
				if (response.status === 200) {
					toast.success("Regjistrimi u krye me sukses");
					expenses.reload();
					clearState();
				} else {
					console.error("Expense submission failed");
				}
			} catch (error: any) {
				console.error(
					"An error occurred while submitting the expense:",
					error.response.data
				);
			} finally {
				setExpenseIsSubmitting(false);
			}
		} else {
			setExpenseIsSubmitting(false);
			return;
		}
	};
	const cleanSatateForDriverExpenses = () => {
		setModalValue(0);
		setModalDescription("");
		setDriverId(null);
		setOpenExpenseModalForDrivers(false);
		setSelectedCashRegisterId(0);
	};
	const [cashOutForWrongOrderSubmitting, setCashOutForWrongOrderSubmitting] =
		useState<boolean>(false);

	const createCashOutForWrongOrder = async () => {
		setCashOutForWrongOrderSubmitting(true);
		if (!selectedCashRegisterId || selectedCashRegisterId === 0) {
			toast.error("Nuk keni te zgjedhur nje Cash Register");
			setCashOutForWrongOrderSubmitting(false);
			return;
		}
		if (!modalValue || modalValue <= 0) {
			toast.error("Vlera duhet te jete me e madhe se zero");
			setCashOutForWrongOrderSubmitting(false);

			return;
		}
		if (!driverId || driverId === 0) {
			toast.error("Shoferi eshte i detyrueshem");
			setCashOutForWrongOrderSubmitting(false);

			return;
		}
		const data = {
			cashOut: modalValue,
			comment: modalDescription,
			driverId: driverId,
			cashRegisterId: selectedCashRegisterId,
		};
		try {
			const response = await api.post(
				"CashRegisterTransaction/return-order-cash-transaction",
				data
			);
			if (response.status === 200) {
				cleanSatateForDriverExpenses();
				toast.success("Sukses!");
			}
		} catch (error: any) {
			console.error("Error: ", error.response.data);
		} finally {
			setCashOutForWrongOrderSubmitting(false);
		}
	};

	let modalGridRef = useRef(null);
	const filteredDocumentTypeDropDown = useMemo(() => {
		if (expense) {
			return documentTypeDropDown.filter(
				(item: any) => item.documentTypeGroupId !== 4
			);
		} else if (earning) {
			return documentTypeDropDown.filter(
				(item: any) => item.documentTypeGroupId === 4
			);
		}
	}, [documentTypeDropDown, expense, earning]);

	const onShowOrdersToImportClick = async (event: any) => {
		await showOrdersToImport();
	};
	const validateImportOrdersClick = () => {
		if (!selectedCountry) {
			return false;
		}
		if (!selectedSettlementInternationalImport) {
			return false;
		}
		return true;
	};
	const [
		createOrderPriceForInternationalOrdersSubmitting,
		setCreateOrderPriceForInternationalOrdersSubmitting,
	] = useState<boolean>(false);

	const creatOrderPriceForInternationalOrders = async () => {
		setCreateOrderPriceForInternationalOrdersSubmitting(true);
		if (validateImportOrdersClick()) {
			const body = {
				targetCountryId: parseInt(selectedCountry),
				settlementId: parseInt(selectedSettlementInternationalImport),
			};

			try {
				const response = await api.post(
					"Order/insert-payments-for-international-orders",
					body,
					{
						headers: {
							"Content-Type": "application/json",
						},
					}
				);

				if (response.status === 200) {
					cleanInternationalPaymentModalState();
					toast.success("Pagesat e porosive u insertuan me sukses!");
				}
			} catch (error) {
				console.log(error);
				toast.error("Ndodhi një gabim gjatë përpunimit të pagesave.");
			} finally {
				setCreateOrderPriceForInternationalOrdersSubmitting(false);
			}
		} else {
			setCreateOrderPriceForInternationalOrdersSubmitting(false);
		}
	};

	const cleanInternationalPaymentModalState = () => {
		setOpenInternationalPaymentsModal(false);
		setSelectedCountry(null);
		setSelectedSettlementInternationalImport(null);
	};

	const handleChange = (e: any) => {
		const newValue = e.target.value;
		const regex = /^\d*\.?\d*$/;

		if (regex.test(newValue)) {
			setValue(newValue);
		}
	};

	const defaultColDefOrdersImport = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
		}),
		[]
	);
	const getShowOrdersToImport = async () => {
		if (validateImportOrdersClick()) {
			const body = {
				targetCountryId: parseInt(selectedCountry),
				settlementId: parseInt(selectedSettlementInternationalImport),
			};

			try {
				const response = await api.post(
					"Finance/payments-for-international-orders-select",
					body,
					{
						headers: {
							"Content-Type": "application/json",
						},
					}
				);

				setInternationalOrdersToImport(response.data);
				return response.data;
			} catch (error) {
				console.error(
					"Error fetching cash register grouped report:",
					error
				);
				throw error;
			}
		}
	};

	const showOrdersToImport = async () => {
		const modalData = await getShowOrdersToImport(); //is dynamic, just modify the procedure
		console.log(modalData);
		if (modalData) {
			let colDefs: any = [];
			const keys = Object.keys(modalData[0]);
			keys.forEach((key) => colDefs.push({ field: key }));
			setInternationalOrdersToImportColDefs(colDefs);
		}
	};

	const getZones = useCallback(async () => {
		const allZones = await fetchZonesCached();
		if (allZones) {
			const countries = allZones
				.filter((item: any) => item.levelId === 1)
				.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
			setCountryDropDown(countries);
		}
	}, []);

	useEffect(() => {
		getZones();
	}, [getZones]);
	const fetchInternationalSettlements = async (countryId: number) => {
		try {
			const response = await api.get(
				`Settlement/international-settlements?countryId=${countryId}`
			);
			if (response.status === 200) {
				const formattedSettlements = response.data.map((item: any) => ({
					label: `ID: ${item.id} - ${item.type}`,
					value: item.id,
				}));
				setInternationalSettlements(formattedSettlements);
			} else {
				toast.error("Failed to load international settlements");
			}
		} catch (error) {
			console.error("Error fetching international settlements:", error);
			toast.error("Error fetching international settlements");
		}
	};
	const handleCountryChange = (value: number) => {
		setSelectedCountry(value);
		if (value) {
			fetchInternationalSettlements(value);
		} else {
			setInternationalSettlements([]);
		}
	};
	return (
		<>
			<div className={classes.mainContainer}>
				{/* <FinanceStatistic sticky={true} stickyTop="20px" /> */}
				<div className={classes.heroContainer}>
					<div className={classes.heroMain}>
						{/* <div className="flex flex-col items-start justify-between gap-4"> */}
						<h1 className={classes.heroTitle}>{strings?.title}</h1>

						<div
							className={
								classes.registerPaymentsAndSubjectButtonDiv
							}
						>
							<Button
								className={classes.registerButton}
								onClick={() => setOpenSubjectModal(true)}
								icon={<Plus />}
							>
								{strings?.registerButton}
							</Button>
							<Button
								className={classes.registerButton}
								onClick={() =>
									setOpenInternationalPaymentsModal(true)
								}
								icon={<Export />}
							>
								{strings?.internationalPaymentButton}
							</Button>
							<Button
								className={classes.registerButton}
								onClick={() => {
									loadAllUsers();
									setOpenExpenseModalForDrivers(true);
								}}
								icon={<ExcelIcon />}
							>
								{strings?.createExpenseForDriver}
							</Button>
						</div>
					</div>
					<div className={classes.firstContainer}>
						<h2 className={classes.registerTitle}>
							{strings?.registerTitle}
						</h2>
						<div className="flex flex-col items-start gap-2">
							<div className="flex flex-row justify-around gap-2">
								{canCreateExpenses && (
									<DefaultSwitch
										label={strings?.switches.expense}
										checked={expense}
										onChange={handleSwitchChange}
									/>
								)}
								{canCreateEarning && (
									<DefaultSwitch
										label={strings?.switches.earning}
										checked={earning}
										onChange={handleSwitchChange}
									/>
								)}
							</div>
						</div>
						<div className={classes.firstInputs}>
							<DefaultSelect
								label={strings?.inputs.type}
								flexDirection="col"
								alignCenter="start"
								className={classes.input}
								options={filteredDocumentTypeDropDown}
								onChange={setDocumentType}
								value={documentType}
							/>
							<DefaultInput
								label={
									strings?.inputs.amount + CONFIG?.CURRENCY
								}
								className={classes.input}
								value={value}
								onChange={handleChange}
							/>
							<div className={classes.textAreaDiv}>
								<p>{strings?.inputs.description}</p>
								<TextArea
									className={classes.textarea}
									value={expenseReason}
									onChange={(e) =>
										setExpenseReason(e.target.value)
									}
									placeholder={strings?.inputs.description}
								/>
							</div>
							<DefaultSelect
								label={strings?.inputs.supplier}
								flexDirection="col"
								alignCenter="start"
								className={classes.input}
								options={subjectsDropDown}
								onChange={setSubjectId}
								value={subjectId}
							/>
						</div>
						<div className={classes.registerButtonDiv}>
							{(canCreateEarning || canCreateExpenses) && (
								<Button
									className={classes.registerButton}
									onClick={submitExpense}
									disabled={expenseIsSubmitting}
								>
									{`${strings?.register} ${
										expense
											? strings?.shpenzimin
											: strings?.hyrjen
									}`}
								</Button>
							)}
						</div>
					</div>
					<div className={classes.secondContainer}>
						<h2 className={classes.registerTitle}>
							{strings?.companyBalance}
						</h2>
						<div className={classes.secondInputs}>
							<DefaultSelect
								className={classes.input}
								label={strings?.inputs.status}
								flexDirection="col"
								alignCenter="start"
								options={expenseStatusOptions}
								value={expenseStatus}
								onChange={(value) => {
									if (!canChangePage()) return;
									if (value === null || value === undefined) {
										setExpenseStatus("");
									} else {
										setExpenseStatus(value);
									}
									setCurrentPage(1);
								}}
								allowClear
							/>
							<DefaultDatePicker
								className={classes.input}
								label={strings?.inputs.fromDate}
								value={fromDate}
								onChange={(date) => {
									if (!canChangePage()) return;
									setCurrentPage(1);
									setFromDate(date);
								}}
							/>
							<DefaultDatePicker
								className={classes.input}
								label={strings?.inputs.toDate}
								value={toDate}
								onChange={(date) => {
									if (!canChangePage()) return;
									setCurrentPage(1);
									setToDate(date);
								}}
							/>
							<DefaultSelect
								label={strings?.inputs.type}
								flexDirection="col"
								alignCenter="start"
								className={classes.input}
								options={documentTypeDropDown}
								onChange={(value) => {
									if (!canChangePage()) return;
									setCurrentPage(1);
									setSelectedDocumentType(value);
								}}
								// null, not undefined: an undefined value would let the select show a choice the
								// unsaved-edits check refused.
								value={selectedDocumentType ?? null}
								allowClear
							/>
						</div>
						<div className={classes.tableInputs}>
							<DefaultSelect
								label={strings?.inputs.cashRegister.label}
								placeholder={
									strings?.inputs.cashRegister.placeholder
								}
								width={355}
								options={cashRegisterDropDown}
								onChange={(value) => setCashRegisterId(value)}
								allowClear={true}
								onClear={onSelectClear}
								flexDirection="col"
								alignCenter="start"
								className={classes.input}
							/>
							<div className={classes.buttons}>
								<Button
									className={classes.button}
									onClick={onExportSelectedId}
								>
									<PrintIcon /> {strings?.excel}
								</Button>
								<Button className={classes.button}>
									<PrintIcon />
									{strings?.print}
								</Button>
							</div>
						</div>
						<PaginationTable
							current={currentPage}
							pageSize={pageSize}
							total={expenses.totalCount}
							startOrderIndex={expenses.firstRow}
							endOrderIndex={expenses.lastRow}
							onChange={onPageChanged}
							onShowSizeChange={handlePageSizeChange}
						/>
						<div
							className="ag-theme-alpine"
							style={{
								height: 400,
								width: "100%",
								overflow: "auto",
							}}
						>
							<AgGridReact
								rowData={expenses.items}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								// paginationPageSize={4}
								undoRedoCellEditing={true}
								undoRedoCellEditingLimit={20}
								suppressCopyRowsToClipboard={true}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								enableRangeSelection={true}
								rowSelection="multiple"
								ref={grid}
								cacheQuickFilter={true}
								onCellClicked={onCellClicked}
								editType={"fullRow"}
								onRowValueChanged={(event) => {
									onRowEdit(event);
								}}
								onFirstDataRendered={onFirstDataRendered}
							/>
						</div>
						{showButtons && (
							<div className="flex justify-end  p-2">
								<Buttons
									button1Text={strings?.cancel}
									button2Text={strings?.save}
									onButton2Click={saveEditedRows}
									onButton1Click={cancleEditedRows}
									disabled={saveEditedRowsSubmitting}
									firstButtonDisabled={
										saveEditedRowsSubmitting
									}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
			<Modal
				open={open}
				title={strings?.modals?.confirmTitle}
				onCancel={() => setOpen(false)}
				onOk={ValidateExpenseAndCreateCashRegister}
				okButtonProps={{
					className: "bg-blue-500",
					disabled: validateExpenseSubmitting,
				}}
				cancelButtonProps={{
					disabled: validateExpenseSubmitting,
				}}
			></Modal>

			<Modal
				open={openSubjectModal}
				title={strings?.modals?.registerSupplier?.title}
				onCancel={() => setOpenSubjectModal(false)}
				width={950}
				footer={() => (
					<div className="flex  justify-end">
						<Button
							type="primary"
							size="large"
							// className="bg-blue-600"
							style={{ backgroundColor: "#4F16C2" }}
							onClick={subjectInsert}
							disabled={subjectInsertSubmitting}
						>
							{strings?.modals?.registerSupplier?.createButton}
						</Button>
					</div>
				)}
			>
				<Divider />
				<div className={classes.modalSubject}>
					<DefaultInput
						className={classes.registerInput}
						label={strings?.inputs?.subjectForm?.description?.label}
						placeholder={
							strings?.inputs?.subjectForm?.description
								?.placeholder
						}
						value={description}
						onChange={(e) => setDescription(e.target.value)}
					/>
					<DefaultInput
						className={classes.registerInput}
						label={strings?.inputs?.subjectForm?.fiscalNo?.label}
						value={fiscalNo}
						onChange={(e) => setFiscalNo(e.target.value)}
						placeholder={
							strings?.inputs?.subjectForm?.fiscalNo?.placeholder
						}
					/>
					<DefaultInput
						className={classes.registerInput}
						label={strings?.inputs?.subjectForm?.vatNo?.label}
						value={vatNo}
						onChange={(e) => setVatNo(e.target.value)}
						placeholder={
							strings?.inputs?.subjectForm?.vatNo?.placeholder
						}
					/>
					<DefaultInput
						className={classes.registerInput}
						label={strings?.inputs?.subjectForm?.email?.label}
						placeholder={
							strings?.inputs?.subjectForm?.email?.placeholder
						}
						value={email}
						onChange={(e) => setEmail(e.target.value)}
					/>
					<DefaultPhoneInput
						className={classes.registerInputPhone}
						label={strings?.inputs?.subjectForm?.phone?.label}
						placeholder={
							strings?.inputs?.subjectForm?.phone?.placeholder
						}
						initialPhoneNumber={phone}
						onPhoneNumberChange={(v) => setPhone(v)}
					/>
					<DefaultInput
						className={classes.registerInput}
						label={strings?.inputs?.subjectForm?.address?.label}
						placeholder={
							strings?.inputs?.subjectForm?.address?.placeholder
						}
						value={address}
						onChange={(e) => setAddress(e.target.value)}
					/>
					<div className="flex flex-col items-start gap-2">
						<p>{strings?.inputs?.subjectForm?.status}</p>
						<DefaultSwitch
							label={strings?.inputs?.subjectForm?.activeSupplier}
							checked={status}
							onChange={(checked) => setStatus(checked)}
						/>
					</div>
				</div>

				<Divider />
			</Modal>
			<Modal
				open={openExpenseModalForDrivers}
				title={strings?.modals?.registerPayment?.title}
				onCancel={cleanSatateForDriverExpenses}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				width={400}
			>
				<Divider />
				<div className="flex flex-col gap-4 items-center">
					<div className="flex flex-col gap-4">
						<DefaultSelect
							label={strings?.inputs?.cashRegister?.label}
							placeholder={
								strings?.inputs?.cashRegister?.placeholder
							}
							width={355}
							options={cashRegisterDropDown}
							onChange={(value) =>
								setSelectedCashRegisterId(value)
							}
							allowClear={true}
							value={selectedCashRegisterId}
							onClear={onSelectClear}
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
						/>
						<DefaultSelect
							label={strings?.driver}
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
							options={driverDropDown}
							onChange={setDriverId}
							value={driverId}
							allowClear={true}
						/>
						<DefaultInput
							label={strings?.inputs?.amount}
							className={classes.input}
							value={modalValue}
							onChange={(e) => setModalValue(e.target.value)}
						/>
						<div className={classes.textAreaDiv}>
							<p>{strings?.inputs?.description}</p>
							<TextArea
								className={classes.textarea}
								value={modalDescription}
								onChange={(e) =>
									setModalDescription(e.target.value)
								}
							/>
						</div>
					</div>
					<Buttons
						button1Text={strings?.close}
						button2Text={strings?.save}
						onButton1Click={cleanSatateForDriverExpenses}
						onButton2Click={createCashOutForWrongOrder}
						disabled={cashOutForWrongOrderSubmitting}
					/>
				</div>
			</Modal>
			<Modal
				open={openInternationalPaymentsModal}
				title={strings?.modals?.internationalPayment?.title}
				onCancel={cleanInternationalPaymentModalState}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				width={1000}
			>
				<Divider />
				<div className="flex flex-col items-center justify-between gap-4">
					<div>
						<DefaultSelect
							label={"Shteti"}
							placeholder={strings?.chooseCountry}
							options={countryDropDown}
							onChange={(value) => handleCountryChange(value)}
							value={selectedCountry}
							allowClear
							className={classes.input}
							flexDirection="col"
							alignCenter="start"
						/>

						<DefaultSelect
							label={"Barazimet"}
							options={internationalSettlements}
							onChange={(value) =>
								setSelectedSettlementInternationalImport(value)
							}
							value={selectedSettlementInternationalImport}
							allowClear
							className={classes.input}
							flexDirection="col"
							alignCenter="start"
						/>

						<Button
							className="button1"
							onClick={onShowOrdersToImportClick}
						>
							{"Shfaq"}
						</Button>
						<Buttons
							button1Text={strings?.close}
							button2Text={strings?.save}
							onButton1Click={cleanInternationalPaymentModalState}
							onButton2Click={
								creatOrderPriceForInternationalOrders
							}
							disabled={
								createOrderPriceForInternationalOrdersSubmitting
							}
							firstButtonDisabled={
								createOrderPriceForInternationalOrdersSubmitting
							}
						/>
					</div>
					<div>
						<div
							className="ag-theme-alpine"
							style={{
								height: 600,
								width: 900,
								display:
									internationalOrdersToImport?.length === 0
										? "none"
										: "",
							}}
						>
							<AgGridReact
								rowData={internationalOrdersToImport}
								ref={modalGridRef}
								columnDefs={internationalOrdersToImportColDefs}
								defaultColDef={defaultColDefOrdersImport}
							/>
						</div>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default AdminFinance;
